import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";
import { triggerBlobDownload } from "@/lib/utils";
import { detailMessage } from "@/lib/zoomQueries";

/**
 * Session Room Tools — a coach-controlled manual stopwatch and a
 * collaborative Notes scratchpad, both added to a Session's Prep plan
 * as resource_type="tool" and rendered inside SessionRoomRenderer.jsx.
 * Mirrors zoomQueries.js's `request()` convention and useBlockPresence's
 * ticket -> EventSource pattern exactly (EventSource cannot send an
 * Authorization header, so a short-lived single-use ticket is minted
 * first over a normal, header-authenticated POST).
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(detailMessage(body, "request_failed"));
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const timerKey = (wrk, ses) => ["session-tool-timer", wrk, ses];
const notesKey = (wrk, ses) => ["session-tool-notes", wrk, ses];

/** Imperative, not a hook — triggered directly from a click handler
 * (Session Room tool tab or a Session card/history row). Fetches the
 * PDF as a blob (a plain <a href> can't carry the Authorization
 * header) and hands it to the browser's normal download flow, using
 * the server's own Content-Disposition filename (date + session title)
 * when present. */
export async function downloadSessionNotesPdf(workspaceId, sesId) {
  const res = await rrFetch(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/tools/notes/export`);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(detailMessage(body, "export_failed"));
  }
  const disposition = res.headers.get("content-disposition") || "";
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/);
  const blob = await res.blob();
  triggerBlobDownload(blob, filenameMatch ? filenameMatch[1] : `session-notes-${sesId}.pdf`);
}

/** Never let an out-of-order response (a slow poll resolving after a
 * newer PUT/SSE event, or a late SSE event arriving after a newer poll)
 * overwrite fresher state — compare server `updated_at` and keep
 * whichever side is newer. Missing timestamps are trusted as-is (e.g.
 * a first-ever read before anything has a value to compare against). */
function preferFresher(incoming, current) {
  if (!incoming) return current;
  if (!current?.updated_at || !incoming?.updated_at) return incoming;
  return new Date(incoming.updated_at) >= new Date(current.updated_at) ? incoming : current;
}

export function useSessionTimer(workspaceId, sesId, { enabled = true } = {}) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: timerKey(workspaceId, sesId),
    queryFn: async () => preferFresher(
      await request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/tools/timer`),
      qc.getQueryData(timerKey(workspaceId, sesId)),
    ),
    enabled: enabled && !!workspaceId && !!sesId,
    refetchInterval: 10000, // polling fallback — SSE (useSessionToolsStream) is the primary transport
  });
}

function useTimerAction(workspaceId, sesId, action) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/tools/timer/${action}`, { method: "POST" }),
    onSuccess: (data) => qc.setQueryData(timerKey(workspaceId, sesId), (current) => preferFresher(data, current)),
  });
}
export const useStartTimer = (workspaceId, sesId) => useTimerAction(workspaceId, sesId, "start");
export const usePauseTimer = (workspaceId, sesId) => useTimerAction(workspaceId, sesId, "pause");
export const useResetTimer = (workspaceId, sesId) => useTimerAction(workspaceId, sesId, "reset");

export function useSessionNotes(workspaceId, sesId, { enabled = true } = {}) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: notesKey(workspaceId, sesId),
    queryFn: async () => preferFresher(
      await request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/tools/notes`),
      qc.getQueryData(notesKey(workspaceId, sesId)),
    ),
    enabled: enabled && !!workspaceId && !!sesId,
    refetchInterval: 10000,
  });
}

export function useUpdateSessionNotes(workspaceId, sesId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content) => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/tools/notes`, { method: "PUT", body: JSON.stringify({ content }) }),
    onSuccess: (data) => qc.setQueryData(notesKey(workspaceId, sesId), (current) => preferFresher(data, current)),
  });
}

/** Live push for both tools sharing one Session-scoped SSE channel —
 * each event carries {tool: "timer"|"notes", ...freshState}, written
 * straight into the matching query's cache (no extra round trip),
 * guarded by the same `preferFresher` monotonicity check as the poll.
 * Reconnects with capped exponential backoff on any transport hiccup
 * (a fresh ticket is minted on every attempt) rather than giving up —
 * the 10s poll on each hook is only the fallback while a reconnect is
 * in flight, not a permanent replacement for the whole session. */
export function useSessionToolsStream({ workspaceId, sesId, enabled }) {
  const qc = useQueryClient();
  const sourceRef = useRef(null);

  useEffect(() => {
    if (!enabled || !workspaceId || !sesId) return undefined;
    let closed = false;
    let retryTimer = null;
    let attempt = 0;

    function scheduleReconnect() {
      if (closed) return;
      attempt += 1;
      const delayMs = Math.min(1000 * 2 ** attempt, 15000);
      retryTimer = setTimeout(connect, delayMs);
    }

    async function connect() {
      if (closed) return;
      let ticket;
      try {
        const res = await rrFetch(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/tools/stream-ticket`, { method: "POST" });
        if (!res.ok) throw new Error("ticket_failed");
        ({ ticket } = await res.json());
      } catch {
        scheduleReconnect();
        return;
      }
      if (closed || !ticket) return;

      const base = process.env.REACT_APP_BACKEND_URL;
      const url = `${base}/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/tools/stream?ticket=${encodeURIComponent(ticket)}`;
      let source;
      try {
        source = new window.EventSource(url);
      } catch {
        scheduleReconnect();
        return;
      }
      if (closed) {
        source.close();
        return;
      }
      sourceRef.current = source;
      source.onopen = () => {
        attempt = 0;
      };
      source.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.tool === "timer") {
            const incoming = { status: data.status, elapsed_ms: data.elapsed_ms, updated_at: data.updated_at };
            qc.setQueryData(timerKey(workspaceId, sesId), (current) => preferFresher(incoming, current));
          } else if (data.tool === "notes") {
            const incoming = { content: data.content, updated_at: data.updated_at };
            qc.setQueryData(notesKey(workspaceId, sesId), (current) => preferFresher(incoming, current));
          }
        } catch {
          // malformed event — the 10s poll on each hook will catch up regardless
        }
      };
      source.onerror = () => {
        source.close();
        if (sourceRef.current === source) sourceRef.current = null;
        scheduleReconnect();
      };
    }

    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (sourceRef.current) sourceRef.current.close();
    };
  }, [enabled, workspaceId, sesId, qc]);
}
